/**
 * Internal users sync manager for organizational directory
 */

import { BaseSyncManager } from './BaseSyncManager';
import { GraphApiClient } from './GraphApiClient';
import { SyncOptions, SyncType, DeltaResponse } from './types';

export class InternalSyncManager extends BaseSyncManager {
  private isOutlookContext: boolean;

  constructor(isOutlookContext: boolean = true) {
    super(isOutlookContext);
    this.isOutlookContext = isOutlookContext;
  }

  protected getSyncType(): SyncType {
    return 'internal';
  }

  protected getMetaKey(): string {
    return 'internal';
  }

  protected async fetchDataDelta(accessToken: string, deltaToken: string): Promise<any> {
    return GraphApiClient.fetchInternalUsersDelta(accessToken, deltaToken);
  }

  protected async fetchDataPage(accessToken: string, skipToken: string, top: number): Promise<any> {
    return GraphApiClient.fetchInternalUsersPage(accessToken, skipToken, top);
  }

  protected async processDeltaQuery(accessToken: string, deltaToken: string): Promise<DeltaResponse> {
    console.log('Processing internal delta query to get final delta token...');
    
    let currentDeltaToken = deltaToken;
    let nextLink: string | null = null;
    let finalDeltaToken: string | null = null;
    let pageCount = 0;
    
    do {
      pageCount++;
      console.log(`Processing internal delta query page ${pageCount}...`);
      
      const response = await this.fetchDataDelta(accessToken, currentDeltaToken);
      
      // Update next link and delta token
      nextLink = response['@odata.nextLink'];
      finalDeltaToken = response['@odata.deltaLink'];
      
      console.log(`Internal page ${pageCount} response:`, {
        hasNextLink: !!nextLink,
        hasDeltaLink: !!finalDeltaToken,
        userCount: response.value?.length || 0
      });
      
      // If we have a next link, use it for the next iteration
      if (nextLink) {
        currentDeltaToken = nextLink;
      }
      
      // Safety check to prevent infinite loops
      if (pageCount > 50) {
        console.warn('Internal delta query processing stopped after 50 pages to prevent infinite loop');
        break;
      }
      
      // Add a small delay between pages to prevent overwhelming the API
      if (nextLink) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
    } while (nextLink);
    
    console.log(`Internal delta query processing complete after ${pageCount} pages`);
    return { deltaToken: finalDeltaToken };
  }

  protected getSourceType(): string {
    return 'internal';
  }

  /**
   * Filter users to only include members with email addresses and company info
   */
  private filterValidUsers(users: any[]): any[] {
    return users.filter((user: any) => {
      if (user.userType !== 'Member') return false;
      if (user.accountEnabled === false) return false;
      if (!user.mail || user.mail.trim() === '') return false;
      
      // Check email domain (case-insensitive)
      const email = user.mail.trim().toLowerCase();
      if (!email.endsWith('@belam.lv')) return false;
      
      // Check if user part contains "noreply" (Outlook only)
      if (this.isOutlookContext) {
        const [userPart] = email.split('@');
        if (userPart.toLowerCase().includes('noreply')) return false;
      }
      
      // Check company info
      if (!(user.companyName || user.officeLocation)) return false;
      if (user.companyName?.trim() === '' && user.officeLocation?.trim() === '') return false;
      
      return true;
    });
  }

  /**
   * Bootstrap sync for internal users - initial full download of organizational users
   */
  async bootstrapInternalSync(
    accessToken: string, 
    options: SyncOptions = {}
  ): Promise<void> {
    console.log('bootstrapInternalSync called with:', { hasAccessToken: !!accessToken });
    
    if (this.isSyncing && !options.skipSearchIndex) {
      throw new Error('Sync already in progress');
    }

    this.isSyncing = true;
    const { onProgress, onError, batchSize = 500 } = options;

    // Set a timeout to prevent sync from getting stuck
    this.setSyncTimeout(5 * 60 * 1000, () => {
      onError?.(new Error('Internal bootstrap sync timeout'));
    });

    try {
      onProgress?.({
        current: 0,
        total: 0,
        phase: 'bootstrap',
        message: 'Starting internal users bootstrap sync...'
      });

      // Check if internal bootstrap is already complete
      const meta = await this.store.getMeta();
      if (meta.internalBootstrapComplete) {
        onProgress?.({
          current: 1,
          total: 1,
          phase: 'bootstrap',
          message: 'Internal bootstrap already complete, skipping...'
        });
        return;
      }

      // Use fast pagination for initial sync, then get delta token
      console.log('Starting fast internal bootstrap sync...');
      const initialResponse = await this.fetchDataPage(accessToken, '', 999);
      
      let processedUsers = 0;
      let nextLink = initialResponse['@odata.nextLink'];
      let deltaLink = initialResponse['@odata.deltaLink'];
      
      console.log('Internal initial response:', {
        hasNextLink: !!nextLink,
        hasDeltaLink: !!deltaLink,
        userCount: initialResponse.value?.length || 0
      });

      onProgress?.({
        current: 0,
        total: 0, // We don't know total yet
        phase: 'bootstrap',
        message: 'Starting internal users bootstrap sync...'
      });

      // Process initial batch - filter for members with email addresses and company info
      const initialUsers = this.filterValidUsers(initialResponse.value || []);
      console.log(`Internal users after filtering: ${initialUsers.length} out of ${initialResponse.value?.length || 0}`);
      
      // Log available fields from first user
      if (initialResponse.value && initialResponse.value.length > 0) {
        console.log('Available fields from Graph API:', Object.keys(initialResponse.value[0]));
      }
      
      
      const normalizedUsers = await this.normalizer.normalizeContacts(initialUsers, 'internal', 'internal');
      console.log(`Normalized internal users: ${normalizedUsers.length}`);
      
      if (normalizedUsers.length > 0) {
        console.log('Sample normalized internal user:', normalizedUsers[0]);
      }
      
      
      await this.store.bulkUpsertContacts(normalizedUsers);
      processedUsers += initialUsers.length;

      onProgress?.({
        current: processedUsers,
        total: 0, // Still unknown
        phase: 'bootstrap',
        message: `Processed ${processedUsers} internal users so far...`
      });

      // Process remaining pages
      while (nextLink) {
        const response = await GraphApiClient.fetchContactsPageFromUrl(accessToken, nextLink);
        const users = this.filterValidUsers(response.value || []);
        
        if (users.length > 0) {
          const normalizedUsers = await this.normalizer.normalizeContacts(users, 'internal', 'internal');
          
          await this.store.bulkUpsertContacts(normalizedUsers);
          processedUsers += users.length;

          onProgress?.({
            current: processedUsers,
            total: 0, // Unknown total
            phase: 'bootstrap',
            message: `Processed ${processedUsers} internal users so far...`
          });
        }

        nextLink = response['@odata.nextLink'];
        deltaLink = response['@odata.deltaLink'];
      }

      // Get delta token for future incremental syncs (skip if requested)
      let finalDeltaToken = deltaLink;
      if (!finalDeltaToken && !options.skipDeltaToken) {
        console.log('Getting internal delta token for future incremental syncs...');
        try {
          // Process delta query to get the final delta token (no progress updates)
          const deltaResponse = await this.processDeltaQuery(accessToken, '');
          finalDeltaToken = deltaResponse.deltaToken;
          console.log('Internal delta token obtained:', finalDeltaToken ? 'Yes' : 'No');
          if (finalDeltaToken) {
            console.log('Internal delta token preview:', finalDeltaToken.substring(0, 100) + '...');
          }
        } catch (error) {
          console.warn('Failed to get internal delta token:', error);
        }
      } else if (finalDeltaToken) {
        console.log('Using existing internal delta link from bootstrap sync');
      } else if (options.skipDeltaToken) {
        console.log('Skipping internal delta token generation (will be done in background)');
      }

      // Update meta with internal bootstrap completion
      await this.markBootstrapComplete(processedUsers, finalDeltaToken);
      
      // Log the delta token for debugging
      if (finalDeltaToken) {
        console.log('Internal bootstrap sync completed with delta token:', finalDeltaToken.substring(0, 100) + '...');
      } else {
        console.warn('Internal bootstrap sync completed but no delta token was obtained');
      }

      // Build search index after internal bootstrap sync
      await this.buildSearchIndex(options);

      // Only show progress if not skipping search index (i.e., not in parallel mode)
      if (!options.skipSearchIndex) {
        onProgress?.({
          current: processedUsers,
          total: processedUsers,
          phase: 'bootstrap',
          message: `Internal bootstrap sync complete! ${processedUsers} users synced`
        });
      }

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Internal bootstrap sync failed');
      console.error('Internal bootstrap sync failed:', err);
      onError?.(err);
      throw err;
    } finally {
      this.isSyncing = false;
      this.clearSyncTimeout();
    }
  }

  /**
   * Incremental sync for internal users using delta tokens
   */
  async incrementalInternalSync(
    accessToken: string,
    options: SyncOptions = {}
  ): Promise<void> {
    this.isSyncing = true;
    const { onProgress, onError } = options;

    try {
      const meta = await this.store.getMeta();
      
      
      if (!meta.internalBootstrapComplete) {
        throw new Error('Internal bootstrap sync must be completed before incremental sync');
      }

      if (!meta.internalDeltaToken) {
        console.warn('No internal delta token available; attempting regeneration...');
        try { await this.generateDeltaToken(accessToken); } catch {}
        const refreshed = await this.store.getMeta();
        if (!refreshed.internalDeltaToken) {
          console.error('Internal delta token still missing after regeneration; skipping incremental run');
          return;
        }
      }

      onProgress?.({
        current: 0,
        total: 0,
        phase: 'incremental',
        message: 'Starting internal users incremental sync...'
      });

      // Fetch changes using delta token
      console.log('Starting internal incremental sync with delta token:', (await this.store.getMeta()).internalDeltaToken?.substring(0, 50) + '...');
      let response;
      try {
        response = await this.fetchDataDelta(accessToken, (await this.store.getMeta()).internalDeltaToken as string);
      } catch (error) {
        console.error('Internal delta sync failed; clearing token and attempting regeneration:', error);
        await this.clearInvalidDeltaToken();
        try { await this.generateDeltaToken(accessToken); } catch {}
        const refreshed = await this.store.getMeta();
        if (!refreshed.internalDeltaToken) return;
        response = await this.fetchDataDelta(accessToken, refreshed.internalDeltaToken);
      }
      const users = response.value || [];
      const newDeltaLink = response['@odata.deltaLink'];
      
      console.log('Internal incremental delta response:', {
        hasNewDeltaLink: !!newDeltaLink,
        userCount: users.length,
        hasChanges: users.length > 0
      });

      let addedCount = 0;
      let updatedCount = 0;
      let deletedCount = 0;

      // Separate users into batches for efficient processing
      const usersToUpsert: any[] = [];
      const usersToDelete: string[] = [];

      // Process changes in batches - filter for members (accountEnabled may not be returned by API)
      for (const user of users) {
        if (user['@removed']) {
          usersToDelete.push(user.id);
          deletedCount++;
        } else {
          // Apply same filtering criteria as filterValidUsers
          const email = user.mail?.trim().toLowerCase();
          
          // Build noreply check conditionally based on context
          const noreplyCheck = this.isOutlookContext 
            ? !email.split('@')[0].toLowerCase().includes('noreply')
            : true; // For Teams, allow noreply emails
          
          if (user.userType === 'Member' && 
              user.accountEnabled !== false &&
              email && 
              email !== '' &&
              email.endsWith('@belam.lv') &&
              noreplyCheck &&
              (user.companyName || user.officeLocation) &&
              (user.companyName?.trim() !== '' || user.officeLocation?.trim() !== '')) {
            // Process members with valid belam.lv email addresses and company info
            usersToUpsert.push(user);
          }
        }
      }

      // Batch process deletions
      if (usersToDelete.length > 0) {
        await this.store.bulkMarkContactsDeleted(usersToDelete);
      }

      // Batch process upserts
      if (usersToUpsert.length > 0) {
        const normalizedUsers = await this.normalizer.normalizeContacts(usersToUpsert, 'internal', 'internal');
        
        // Check which users are new vs updates
        const existingUsers = await Promise.all(
          normalizedUsers.map(user => this.store.getContact(user.id))
        );
        
        existingUsers.forEach((existing, index) => {
          if (existing) {
            updatedCount++;
          } else {
            addedCount++;
          }
        });

        await this.store.bulkUpsertContacts(normalizedUsers);
      }

      // Update meta with new delta token and sync time
      await this.updateIncrementalSyncMeta(newDeltaLink, addedCount, deletedCount);
      
      // Rebuild search index if users were added, updated, or deleted
      if (addedCount > 0 || updatedCount > 0 || deletedCount > 0) {
        await this.buildSearchIndex(options);
      }
      
      // Log the delta token for debugging
      if (newDeltaLink) {
        console.log('Internal incremental sync completed with new delta token:', newDeltaLink.substring(0, 100) + '...');
      } else {
        console.warn('Internal incremental sync completed but no new delta token was received, keeping existing one');
      }

      onProgress?.({
        current: users.length,
        total: users.length,
        phase: 'incremental',
        message: `Internal incremental sync complete! +${addedCount} -${deletedCount} ~${updatedCount}`
      });

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Internal incremental sync failed');
      console.error('Internal incremental sync failed:', err);
      onError?.(err);
      throw err;
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Generate delta token for future incremental syncs
   */
  async generateDeltaToken(accessToken: string): Promise<void> {
    console.log('Generating internal delta token...');
    try {
      const meta = await this.store.getMeta();
      let deltaResponse;
      if (meta.internalDeltaToken) {
        // Use existing delta token to get a fresh one (no user download)
        console.log('Refreshing existing internal delta token...');
        deltaResponse = await this.processDeltaQuery(accessToken, meta.internalDeltaToken);
      } else {
        // Generate initial delta token (no user download)
        console.log('Generating initial internal delta token...');
        deltaResponse = await this.processDeltaQuery(accessToken, '');
      }
      if (deltaResponse.deltaToken) {
        // Re-read metadata to get latest state including Corporate/Personal delta tokens
        const latestMeta = await this.store.getMeta();
        
        // Update meta with the new delta token and ensure bootstrap is complete
        await this.store.setMeta({
          ...latestMeta,  // Use latest metadata instead of stale meta
          internalDeltaToken: deltaResponse.deltaToken,
          internalBootstrapComplete: true // Ensure bootstrap is marked complete
        });
        console.log('Internal delta token generated successfully');
      }
    } catch (error) {
      console.warn('Failed to generate internal delta token:', error);
    }
  }
}
